import { logger } from '../../utils/logger';
/**
 * Impersonation Banner Component
 *
 * Fixed banner displayed at the top of the viewport when an admin is
 * impersonating a user. Shows the impersonated user's info and provides
 * a button to end the impersonation session.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { endImpersonation } from '../../store/slices/impersonationSlice';

/**
 * Banner shown when admin is impersonating a user
 */
export function ImpersonationBanner() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { isImpersonating, session, isLoading } = useSelector(
    (state: RootState) => state.impersonation
  );

  const handleEndImpersonation = useCallback(async () => {
    try {
      await dispatch(endImpersonation()).unwrap();
      // Redirect back to admin user management after ending impersonation
      navigate('/admin/users');
    } catch (error) {
      logger.error('Failed to end impersonation:', error);
      // Still navigate back even if there's an error
      navigate('/admin/users');
    }
  }, [dispatch, navigate]);

  // Don't render if not impersonating
  if (!isImpersonating || !session) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 shadow-lg"
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Left side - Warning and user info */}
          <div className="flex items-center gap-3">
            {/* Warning icon */}
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-amber-900"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Text content */}
            <div className="flex items-center gap-2 text-amber-900 font-medium text-sm">
              <span className="font-bold">IMPERSONATION ACTIVE</span>
              <span className="hidden sm:inline">|</span>
              <span className="hidden sm:inline">
                Viewing as:{' '}
                <span className="font-semibold">
                  {session.targetUser.displayName} ({session.targetUser.email})
                </span>
              </span>
              <span className="sm:hidden">{session.targetUser.email}</span>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-3">
            {/* All actions logged indicator */}
            <span className="hidden md:inline text-amber-800 text-xs">All actions are logged</span>

            {/* End impersonation button */}
            <button
              type="button"
              onClick={handleEndImpersonation}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-700 
                         rounded-md text-sm font-medium text-amber-900 bg-amber-100 
                         hover:bg-amber-200 focus:outline-none focus:ring-2 
                         focus:ring-offset-2 focus:ring-amber-500
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors duration-150"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Ending...</span>
                </>
              ) : (
                <>
                  <svg
                    className="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 4.25A2.25 2.25 0 015.25 2h5.5A2.25 2.25 0 0113 4.25v2a.75.75 0 01-1.5 0v-2a.75.75 0 00-.75-.75h-5.5a.75.75 0 00-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 00.75-.75v-2a.75.75 0 011.5 0v2A2.25 2.25 0 0110.75 18h-5.5A2.25 2.25 0 013 15.75V4.25z"
                      clipRule="evenodd"
                    />
                    <path
                      fillRule="evenodd"
                      d="M19 10a.75.75 0 00-.75-.75H8.704l1.048-.943a.75.75 0 10-1.004-1.114l-2.5 2.25a.75.75 0 000 1.114l2.5 2.25a.75.75 0 101.004-1.114l-1.048-.943h9.546A.75.75 0 0019 10z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>End Impersonation</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
